import { useCallback } from "react";
import type { UnitCategory } from "verdi-common/Configurations/SensorConfigurations/SensorConfigurationTypes";
import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import type { Unit } from "verdi-common/Configurations/Units/UnitConfigurations";
import { UnitConfigurations } from "verdi-common/Configurations/Units/UnitConfigurations";
import { convertToUnit } from "verdi-common/Configurations/Units/UnitConversion";
import type { UnitPreferences } from "verdi-common/Configurations/Units/UnitPresetsConfigurations";
import { useUnitPreferences } from "@/hooks/use-unit-preferences";
import { createDashboardLogger, DashboardModuleNames } from "@/logging";
import { useDataViewerStore } from "../data-viewer-provider";

const Logger = createDashboardLogger(DashboardModuleNames.dataViewer);

export type DisplayInfo = {
    /** The resolved display unit, or `null` when the category is unknown. */
    unit: Unit | null;
    /** The unit symbol, or `""` when the category is unknown. */
    symbol: string;
    /** False when the category has no configuration (arrived over the wire but not in verdi-common yet). */
    isKnown: boolean;
    /** Converts a raw database value to the display unit. Identity when the category is unknown. */
    fromDatabase: (value: number) => number;
};

export type GetDisplayInfo = (unitCategory: string) => DisplayInfo;

const UNKNOWN_DISPLAY_INFO: DisplayInfo = {
    unit: null,
    symbol: "",
    isKnown: false,
    fromDatabase: (v) => v,
};

// unitCategory is `string` over the wire (backend types it as z.string()); a new category can arrive
// before verdi-common enumerates it. Warn once per unknown category rather than per render/axis.
const warnedUnknownCategories = new Set<string>();

/**
 * Pure resolver for a category's display info. Extracted from the hook so it can be unit-tested
 * without React. Falls back to a safe identity mapping (raw values, no symbol) for unknown categories
 * so the app degrades gracefully instead of crashing on an unrecognised `unitCategory`.
 */
export function buildDisplayInfo(
    unitCategory: string,
    unitPreferences: UnitPreferences,
    unitOverrides: Partial<Record<UnitCategory, Unit>>,
): DisplayInfo {
    const categoryConfig = getUnitCategoryConfiguration(unitCategory);
    if (!categoryConfig) {
        if (!warnedUnknownCategories.has(unitCategory)) {
            warnedUnknownCategories.add(unitCategory);
            Logger.warn(
                { unitCategory },
                "unknown unitCategory from backend; rendering raw values — add it to verdi-common UnitCategoryConfigurations",
            );
        }
        return UNKNOWN_DISPLAY_INFO;
    }

    // The guard proved membership, so indexing the preference/override records is sound.
    const unit = unitOverrides[unitCategory as UnitCategory] ?? unitPreferences[unitCategory as UnitCategory];
    const databaseUnit = categoryConfig.databaseUnit;
    const symbol = UnitConfigurations[unit]?.symbol ?? ""; // defense-in-depth on the unit-level lookup

    const fromDatabase: (v: number) => number =
        unit === databaseUnit
            ? (v) => v
            : (v) => convertToUnit({ value: v, currentUnit: databaseUnit, targetUnit: unit, round: false }).value;

    return { unit, symbol, isKnown: true, fromDatabase };
}

/**
 * Returns a stable `getDisplayInfo` callback backed by the user's unit preferences,
 * with per-category overrides from the data-viewer store applied on top.
 */
export function useGetDisplayInfo(): GetDisplayInfo {
    const { unitPreferences } = useUnitPreferences();
    const unitOverrides = useDataViewerStore((state) => state.unitOverrides);

    return useCallback(
        (unitCategory: string): DisplayInfo => buildDisplayInfo(unitCategory, unitPreferences, unitOverrides),
        [unitPreferences, unitOverrides],
    );
}
